/**
 * ScreenNoticeHUD.ts
 * 
 * 월드 공지, 서버 점검, 업데이트 등 중요 알림을 화면 중앙 상단에
 * 화려하고 눈에 띄는 애니메이션 배너로 푸시 표시하는 HUD
 */

export class ScreenNoticeHUD {
  private static containerEl: HTMLElement | null = null;
  private static hideTimer: number | null = null;

  public static mount(): void {
    if (this.containerEl) return;
    const el = document.createElement('div');
    el.id = 'screen-notice-hud';
    el.style.cssText =
      'position:fixed;top:65px;left:50%;transform:translateX(-50%);z-index:99999;' +
      'display:none;pointer-events:none;text-align:center;max-width:90vw;width:580px;';
    document.body.appendChild(el);
    this.containerEl = el;
  }

  /**
   * 화면 중앙에 공지 배너를 팝업 푸시
   * @param title 타이틀 (예: 📢 [월드 공지], 🚨 [긴급 서버점검], 🔄 [클라이언트 업데이트])
   * @param message 내용 본문
   * @param durationSeconds 노출 시간 (기본 6초)
   * @param type 배너 색상 스타일 ('notice' | 'warning' | 'alert' | 'update')
   */
  public static show(
    title: string,
    message: string,
    durationSeconds = 6,
    type: 'notice' | 'warning' | 'alert' | 'update' = 'notice'
  ): void {
    this.mount();
    if (!this.containerEl) return;

    if (this.hideTimer !== null) {
      window.clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }

    let borderColor = '#38bdf8';
    let titleColor = '#fde047';
    let glowColor = 'rgba(56,189,248,0.5)';
    let bgGradient = 'linear-gradient(180deg, rgba(15,23,42,0.96) 0%, rgba(2,6,23,0.92) 100%)';

    if (type === 'warning' || type === 'alert') {
      borderColor = '#ef4444';
      titleColor = '#fca5a5';
      glowColor = 'rgba(239,68,68,0.6)';
      bgGradient = 'linear-gradient(180deg, rgba(69,10,10,0.96) 0%, rgba(15,23,42,0.92) 100%)';
    } else if (type === 'update') {
      borderColor = '#a855f7';
      titleColor = '#d8b4fe';
      glowColor = 'rgba(168,85,247,0.6)';
      bgGradient = 'linear-gradient(180deg, rgba(59,7,100,0.96) 0%, rgba(15,23,42,0.92) 100%)';
    } else {
      // notice
      borderColor = '#10b981';
      titleColor = '#86efac';
      glowColor = 'rgba(16,185,129,0.5)';
      bgGradient = 'linear-gradient(180deg, rgba(6,78,59,0.96) 0%, rgba(15,23,42,0.92) 100%)';
    }

    this.containerEl.innerHTML = `
      <div style="
        background: ${bgGradient};
        border: 2px solid ${borderColor};
        border-radius: 8px;
        padding: 12px 20px;
        box-shadow: 0 8px 32px rgba(0,0,0,0.8), 0 0 20px ${glowColor};
        backdrop-filter: blur(8px);
        animation: screenNoticeFadeIn 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards;
      ">
        <div style="font-size: 15px; font-weight: 800; color: ${titleColor}; margin-bottom: 4px; letter-spacing: 0.5px; text-shadow: 0 2px 4px rgba(0,0,0,0.8);">
          ${title}
        </div>
        <div style="font-size: 13px; font-weight: 600; color: #f8fafc; line-height: 1.5; word-break: keep-all; text-shadow: 0 1px 3px rgba(0,0,0,0.8);">
          ${message}
        </div>
      </div>
      <style>
        @keyframes screenNoticeFadeIn {
          from { opacity: 0; transform: translateY(-16px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      </style>
    `;

    this.containerEl.style.display = 'block';

    this.hideTimer = window.setTimeout(() => {
      if (this.containerEl) {
        this.containerEl.style.display = 'none';
      }
      this.hideTimer = null;
    }, durationSeconds * 1000);
  }
}
